import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';

const KATEGORIEN_FAHRZEUG = ['Fahrzeugdokument', 'Versicherung', 'Rechnung', 'Foto', 'Sonstiges'];
const KATEGORIEN_KONTAKT  = ['Vollmacht', 'Vertrag', 'Sonstiges'];

function formatDate(str) {
  if (!str) return '–';
  const d = new Date(str);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fileIcon(mimetype, originalname) {
  const ext = (originalname || '').split('.').pop().toUpperCase() || '?';
  let bg = '#94a3b8', label = ext.slice(0, 4);
  if (!mimetype) { /* keep defaults */ }
  else if (mimetype.startsWith('image/')) { bg = '#0ea5e9'; label = 'IMG'; }
  else if (mimetype === 'application/pdf') { bg = '#ef4444'; label = 'PDF'; }
  else if (mimetype.includes('word')) { bg = '#3b82f6'; label = 'DOC'; }
  else if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) { bg = '#22c55e'; label = 'XLS'; }
  else if (mimetype.includes('text')) { bg = '#64748b'; label = 'TXT'; }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: bg, color: '#fff', borderRadius: 5,
      fontSize: 9, fontWeight: 800, letterSpacing: '.5px',
      width: 32, height: 32, flexShrink: 0
    }}>{label}</span>
  );
}

function kategorieBadgeClass(kat) {
  const m = {
    'Fahrzeugdokument': 'badge-blue', 'Versicherung': 'badge-warning',
    'Rechnung': 'badge-gray', 'Foto': 'badge-success',
    'Vollmacht': 'badge-blue', 'Vertrag': 'badge-warning',
    'Sonstiges': 'badge-gray'
  };
  return m[kat] || 'badge-gray';
}

// ─── Foto-Lightbox ────────────────────────────────────────────────────────────

function Lightbox({ src, name, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}
    >
      <div style={{ color: '#fff', marginBottom: 12, fontSize: 13, opacity: .7 }}>{name} — Klicken oder ESC zum Schließen</div>
      <img
        src={src}
        alt={name}
        style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,.5)' }}
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
}

// ─── Upload-Bereich ───────────────────────────────────────────────────────────

function UploadZone({ entityId, isKontakt, kategorien, onUploaded }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [kategorie, setKategorie] = useState(kategorien[kategorien.length - 1]);
  const fileRef = useRef();

  const doUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    setError('');
    const errs = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append('datei', file);
      fd.append('kategorie', kategorie);
      try {
        if (isKontakt) await api.uploadKontaktDokument(entityId, fd);
        else await api.uploadDokument(entityId, fd);
      } catch (e) {
        errs.push(`${file.name}: ${e.message}`);
      }
    }
    setUploading(false);
    if (errs.length) setError(errs.join('\n'));
    onUploaded();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUpload([...e.dataTransfer.files]);
  };

  return (
    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
      {error && <div className="alert alert-error" style={{ marginBottom: 12, whiteSpace: 'pre-wrap' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Kategorie</label>
          <select className="form-select" value={kategorie} onChange={e => setKategorie(e.target.value)} style={{ minWidth: 160 }}>
            {kategorien.map(k => <option key={k}>{k}</option>)}
          </select>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button className="btn btn-primary btn-sm" onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? 'Hochladen…' : '+ Datei auswählen'}
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={e => doUpload([...e.target.files])}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          />
        </div>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--gray-300)'}`,
          borderRadius: 8,
          padding: '16px',
          textAlign: 'center',
          color: dragOver ? 'var(--primary)' : 'var(--gray-400)',
          background: dragOver ? 'var(--primary-light)' : 'transparent',
          transition: 'all .15s',
          fontSize: 13,
          cursor: 'pointer'
        }}
        onClick={() => fileRef.current.click()}
      >
        {uploading
          ? '⏳ Wird hochgeladen…'
          : 'Dateien hier hineinziehen oder klicken zum Auswählen (Bilder, PDF, Word, Excel – max. 20 MB)'
        }
      </div>
    </div>
  );
}

// ─── Haupt-Komponente ─────────────────────────────────────────────────────────

export default function DokumenteSection({ fahrzeugId, kontaktId }) {
  const isKontakt = !!kontaktId;
  const entityId = isKontakt ? kontaktId : fahrzeugId;
  const kategorien = isKontakt ? KATEGORIEN_KONTAKT : KATEGORIEN_FAHRZEUG;

  const [dokumente, setDokumente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [filterKat, setFilterKat] = useState('alle');
  const [viewMode, setViewMode] = useState('list');

  const load = useCallback(() => {
    setLoading(true);
    const promise = isKontakt ? api.getKontaktDokumente(entityId) : api.getDokumente(entityId);
    promise.then(setDokumente).catch(console.error).finally(() => setLoading(false));
  }, [entityId, isKontakt]);

  useEffect(load, [load]);

  const handleDelete = async () => {
    if (isKontakt) await api.deleteKontaktDokument(deleteId);
    else await api.deleteDokument(deleteId);
    setDeleteId(null);
    load();
  };

  const openDoc = (doc) => {
    const url = isKontakt ? api.getKontaktDokumentUrl(doc.id) : api.getDokumentUrl(doc.id);
    if (doc.mimetype && doc.mimetype.startsWith('image/')) {
      setLightbox({ src: url, name: doc.originalname });
    } else {
      window.open(url, '_blank');
    }
  };

  const downloadDoc = (doc) => {
    const url = isKontakt ? api.getKontaktDokumentUrl(doc.id, true) : api.getDokumentUrl(doc.id, true);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.originalname;
    a.click();
  };

  const getUrl = (doc) => isKontakt ? api.getKontaktDokumentUrl(doc.id) : api.getDokumentUrl(doc.id);

  const availableKats = ['alle', ...kategorien.filter(k => dokumente.some(d => d.kategorie === k))];
  const filtered = filterKat === 'alle' ? dokumente : dokumente.filter(d => d.kategorie === filterKat);
  const fotos = filtered.filter(d => d.mimetype && d.mimetype.startsWith('image/'));
  const dateien = filtered.filter(d => !d.mimetype || !d.mimetype.startsWith('image/'));

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {/* Header */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-600)' }}>Dokumente{!isKontakt ? ' & Fotos' : ''}</span>
          <span className="badge badge-gray">{dokumente.length}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {availableKats.length > 2 && (
            <select className="form-select" value={filterKat} onChange={e => setFilterKat(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', height: 28 }}>
              {availableKats.map(k => <option key={k} value={k}>{k === 'alle' ? 'Alle Kategorien' : k}</option>)}
            </select>
          )}
          <div style={{ display: 'flex', border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'hidden' }}>
            {[['grid', '⊞'], ['list', '☰']].map(([m, icon]) => (
              <button key={m} onClick={() => setViewMode(m)} style={{
                padding: '4px 8px', fontSize: 13, border: 'none', cursor: 'pointer',
                background: viewMode === m ? 'var(--primary)' : '#fff',
                color: viewMode === m ? '#fff' : 'var(--gray-600)'
              }}>{icon}</button>
            ))}
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setShowUpload(s => !s)}>
            {showUpload ? '✕ Schließen' : '+ Hochladen'}
          </button>
        </div>
      </div>

      {/* Upload-Zone */}
      {showUpload && <UploadZone entityId={entityId} isKontakt={isKontakt} kategorien={kategorien} onUploaded={load} />}

      {/* Content */}
      {loading ? (
        <div style={{ padding: 20, color: 'var(--gray-400)', fontSize: 13 }}>Lade…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '28px 20px', color: 'var(--gray-400)', fontSize: 13, textAlign: 'center' }}>
          {dokumente.length === 0 ? 'Noch keine Dokumente hochgeladen.' : 'Keine Dokumente in dieser Kategorie.'}
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{ padding: 20 }}>
          {fotos.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>
                Fotos ({fotos.length})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
                {fotos.map(doc => (
                  <div key={doc.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--gray-200)', cursor: 'pointer', background: 'var(--gray-50)' }}
                    onClick={() => openDoc(doc)}>
                    <img
                      src={getUrl(doc)}
                      alt={doc.originalname}
                      style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <div style={{ padding: '6px 8px' }}>
                      <div style={{ fontSize: 11, color: 'var(--gray-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.originalname}</div>
                      <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2 }}>{formatDate(doc.erstellt_am)}</div>
                    </div>
                    <button
                      style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(220,38,38,.85)', border: 'none', borderRadius: 4, color: '#fff', padding: '2px 6px', fontSize: 11, cursor: 'pointer' }}
                      onClick={e => { e.stopPropagation(); setDeleteId(doc.id); }}
                    >✕</button>
                    <span className={`badge ${kategorieBadgeClass(doc.kategorie)}`} style={{ position: 'absolute', bottom: 32, left: 6, fontSize: 10 }}>{doc.kategorie}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {dateien.length > 0 && (
            <>
              {fotos.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Dokumente ({dateien.length})</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {dateien.map(doc => (
                  <div key={doc.id} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: 14, cursor: 'pointer', background: '#fff', position: 'relative', transition: 'box-shadow .15s' }}
                    onClick={() => openDoc(doc)}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                  >
                    <div style={{ fontSize: 32, marginBottom: 8 }}>{fileIcon(doc.mimetype, doc.originalname)}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>{doc.originalname}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{formatSize(doc.groesse)} · {formatDate(doc.erstellt_am)}</div>
                    <div style={{ marginTop: 8 }}><span className={`badge ${kategorieBadgeClass(doc.kategorie)}`} style={{ fontSize: 10 }}>{doc.kategorie}</span></div>
                    <button
                      style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', color: 'var(--gray-300)', fontSize: 14, cursor: 'pointer' }}
                      onClick={e => { e.stopPropagation(); setDeleteId(doc.id); }}
                      onMouseEnter={e => e.target.style.color = 'var(--danger)'}
                      onMouseLeave={e => e.target.style.color = 'var(--gray-300)'}
                    >✕</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Datei</th>
                <th style={thStyle}>Kategorie</th>
                <th style={thStyle}>Größe</th>
                <th style={thStyle}>Hochgeladen</th>
                <th style={thStyle}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(doc => (
                <tr key={doc.id}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 24 }}>{fileIcon(doc.mimetype, doc.originalname)}</span>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--gray-800)', fontSize: 13 }}>{doc.originalname}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{doc.mimetype}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}><span className={`badge ${kategorieBadgeClass(doc.kategorie)}`}>{doc.kategorie}</span></td>
                  <td style={tdStyle}>{formatSize(doc.groesse)}</td>
                  <td style={tdStyle}>{formatDate(doc.erstellt_am)}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openDoc(doc)}>Öffnen</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => downloadDoc(doc)}>↓ Download</button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteId(doc.id)}>Löschen</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lightbox && <Lightbox src={lightbox.src} name={lightbox.name} onClose={() => setLightbox(null)} />}

      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Dokument löschen</span>
              <button className="modal-close" onClick={() => setDeleteId(null)}>✕</button>
            </div>
            <div className="modal-body confirm-dialog">
              <p>Diese Datei wirklich löschen? Sie wird vom Server entfernt.</p>
              <div className="confirm-actions">
                <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={handleDelete}>Löschen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: 'left', padding: '8px 14px',
  fontSize: 11, fontWeight: 600, color: 'var(--gray-500)',
  textTransform: 'uppercase', letterSpacing: '.5px',
  background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)'
};
const tdStyle = { padding: '10px 14px', borderBottom: '1px solid var(--gray-100)', color: 'var(--gray-700)', fontSize: 14, verticalAlign: 'middle' };
